import {assertNever} from '@Utils/assert-never.util';

export interface ImageSize {
  width: number;
  height: number;
}

export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export enum CropWindowHandle {
  TOP_LEFT = 'top-left',
  TOP = 'top',
  TOP_RIGHT = 'top-right',
  RIGHT = 'right',
  BOTTOM_RIGHT = 'bottom-right',
  BOTTOM = 'bottom',
  BOTTOM_LEFT = 'bottom-left',
  LEFT = 'left',
}

export const MIN_CROP_WINDOW_SIDE_PX = 16;

enum HandleAxisDirection {
  START = -1,
  NONE = 0,
  END = 1,
}

interface HandleDirection {
  horizontal: HandleAxisDirection;
  vertical: HandleAxisDirection;
}

interface AxisSpan {
  start: number;
  length: number;
}

const HANDLE_DIRECTIONS: Record<CropWindowHandle, HandleDirection> = {
  [CropWindowHandle.TOP_LEFT]: {horizontal: HandleAxisDirection.START, vertical: HandleAxisDirection.START},
  [CropWindowHandle.TOP]: {horizontal: HandleAxisDirection.NONE, vertical: HandleAxisDirection.START},
  [CropWindowHandle.TOP_RIGHT]: {horizontal: HandleAxisDirection.END, vertical: HandleAxisDirection.START},
  [CropWindowHandle.RIGHT]: {horizontal: HandleAxisDirection.END, vertical: HandleAxisDirection.NONE},
  [CropWindowHandle.BOTTOM_RIGHT]: {horizontal: HandleAxisDirection.END, vertical: HandleAxisDirection.END},
  [CropWindowHandle.BOTTOM]: {horizontal: HandleAxisDirection.NONE, vertical: HandleAxisDirection.END},
  [CropWindowHandle.BOTTOM_LEFT]: {horizontal: HandleAxisDirection.START, vertical: HandleAxisDirection.END},
  [CropWindowHandle.LEFT]: {horizontal: HandleAxisDirection.START, vertical: HandleAxisDirection.NONE},
};

const HALF = 0.5;

export function getFullImageCropRect(imageSize: ImageSize): CropRect {
  return {x: 0, y: 0, width: imageSize.width, height: imageSize.height};
}

export function clampCropRectToImage(rect: CropRect, imageSize: ImageSize): CropRect {
  const width = clampNumber(rect.width, getMinSide(imageSize.width), imageSize.width);
  const height = clampNumber(rect.height, getMinSide(imageSize.height), imageSize.height);

  return {
    x: clampNumber(rect.x, 0, imageSize.width - width),
    y: clampNumber(rect.y, 0, imageSize.height - height),
    width,
    height,
  };
}

export function fitCropRectToAspectRatio(rect: CropRect, ratio: number, imageSize: ImageSize): CropRect {
  const isImageWiderThanRatio = imageSize.width / imageSize.height > ratio;
  const width = isImageWiderThanRatio ? imageSize.height * ratio : imageSize.width;
  const height = isImageWiderThanRatio ? imageSize.height : imageSize.width / ratio;

  return {
    x: clampNumber(getCentreX(rect) - width * HALF, 0, imageSize.width - width),
    y: clampNumber(getCentreY(rect) - height * HALF, 0, imageSize.height - height),
    width,
    height,
  };
}

interface CropRectMove {
  rect: CropRect;
  deltaX: number;
  deltaY: number;
  imageSize: ImageSize;
}

interface CropRectHandleResize {
  rect: CropRect;
  handle: CropWindowHandle;
  pointX: number;
  pointY: number;
  aspectRatio: number | null;
  imageSize: ImageSize;
}

export function moveCropRect({rect, deltaX, deltaY, imageSize}: CropRectMove): CropRect {
  return {
    ...rect,
    x: clampNumber(rect.x + deltaX, 0, imageSize.width - rect.width),
    y: clampNumber(rect.y + deltaY, 0, imageSize.height - rect.height),
  };
}

export function resizeCropRectByHandle({rect, handle, pointX, pointY, aspectRatio, imageSize}: CropRectHandleResize): CropRect {
  const direction = HANDLE_DIRECTIONS[handle];
  if (aspectRatio === null) {
    return resizeFreeCropRect(rect, direction, {pointX, pointY, imageSize});
  }
  return resizeLockedCropRect(rect, direction, {pointX, pointY, aspectRatio, imageSize});
}

export function scaleCropRectBetweenImageSizes(rect: CropRect, fromSize: ImageSize, toSize: ImageSize): CropRect {
  if (fromSize.width <= 0 || fromSize.height <= 0) {
    return getFullImageCropRect(toSize);
  }

  const scaleX = toSize.width / fromSize.width;
  const scaleY = toSize.height / fromSize.height;
  return clampCropRectToImage(
    {
      x: Math.round(rect.x * scaleX),
      y: Math.round(rect.y * scaleY),
      width: Math.round(rect.width * scaleX),
      height: Math.round(rect.height * scaleY),
    },
    toSize
  );
}

export function isCropWindowEdgeHandle(handle: CropWindowHandle): boolean {
  const direction = HANDLE_DIRECTIONS[handle];
  return direction.horizontal === HandleAxisDirection.NONE || direction.vertical === HandleAxisDirection.NONE;
}

interface FreeResizeInput {
  pointX: number;
  pointY: number;
  imageSize: ImageSize;
}

interface LockedResizeInput extends FreeResizeInput {
  aspectRatio: number;
}

function resizeFreeCropRect(rect: CropRect, direction: HandleDirection, input: FreeResizeInput): CropRect {
  const {pointX, pointY, imageSize} = input;
  const horizontal = resizeFreeAxis({start: rect.x, length: rect.width}, direction.horizontal, pointX, imageSize.width);
  const vertical = resizeFreeAxis({start: rect.y, length: rect.height}, direction.vertical, pointY, imageSize.height);
  return {x: horizontal.start, y: vertical.start, width: horizontal.length, height: vertical.length};
}

function resizeFreeAxis(span: AxisSpan, direction: HandleAxisDirection, point: number, imageSide: number): AxisSpan {
  if (direction === HandleAxisDirection.NONE) {
    return span;
  }

  const anchor = getAnchor(span, direction);
  const length = clampNumber(direction * (point - anchor), getMinSide(imageSide), getAvailableLength(anchor, direction, imageSide));
  return {start: getStartFromAnchor(anchor, direction, length), length};
}

function resizeLockedCropRect(rect: CropRect, direction: HandleDirection, input: LockedResizeInput): CropRect {
  const {aspectRatio, imageSize} = input;
  const horizontalAnchor = getAnchor({start: rect.x, length: rect.width}, direction.horizontal);
  const verticalAnchor = getAnchor({start: rect.y, length: rect.height}, direction.vertical);

  const maxWidth = Math.min(
    getAvailableLength(horizontalAnchor, direction.horizontal, imageSize.width),
    getAvailableLength(verticalAnchor, direction.vertical, imageSize.height) * aspectRatio
  );
  const minWidth = Math.min(Math.max(MIN_CROP_WINDOW_SIDE_PX, MIN_CROP_WINDOW_SIDE_PX * aspectRatio), maxWidth);
  const width = clampNumber(getLockedDesiredWidth(direction, horizontalAnchor, verticalAnchor, input), minWidth, maxWidth);
  const height = width / aspectRatio;

  return {
    x: getLockedAxisStart({anchor: horizontalAnchor, direction: direction.horizontal, length: width, centre: getCentreX(rect), imageSide: imageSize.width}),
    y: getLockedAxisStart({anchor: verticalAnchor, direction: direction.vertical, length: height, centre: getCentreY(rect), imageSide: imageSize.height}),
    width,
    height,
  };
}

function getLockedDesiredWidth(direction: HandleDirection, horizontalAnchor: number, verticalAnchor: number, input: LockedResizeInput): number {
  const widthFromPointerX = Math.max(0, direction.horizontal * (input.pointX - horizontalAnchor));
  const widthFromPointerY = Math.max(0, direction.vertical * (input.pointY - verticalAnchor)) * input.aspectRatio;

  if (direction.horizontal === HandleAxisDirection.NONE) {
    return widthFromPointerY;
  }
  if (direction.vertical === HandleAxisDirection.NONE) {
    return widthFromPointerX;
  }
  return Math.max(widthFromPointerX, widthFromPointerY);
}

interface LockedAxisPlacement {
  anchor: number;
  direction: HandleAxisDirection;
  length: number;
  centre: number;
  imageSide: number;
}

function getLockedAxisStart(placement: LockedAxisPlacement): number {
  if (placement.direction === HandleAxisDirection.NONE) {
    return clampNumber(placement.centre - placement.length * HALF, 0, placement.imageSide - placement.length);
  }
  return getStartFromAnchor(placement.anchor, placement.direction, placement.length);
}

function getAnchor(span: AxisSpan, direction: HandleAxisDirection): number {
  return direction === HandleAxisDirection.START ? span.start + span.length : span.start;
}

function getAvailableLength(anchor: number, direction: HandleAxisDirection, imageSide: number): number {
  switch (direction) {
    case HandleAxisDirection.START:
      return anchor;
    case HandleAxisDirection.END:
      return imageSide - anchor;
    case HandleAxisDirection.NONE:
      return imageSide;
    default:
      return assertNever(direction);
  }
}

function getStartFromAnchor(anchor: number, direction: HandleAxisDirection, length: number): number {
  return direction === HandleAxisDirection.START ? anchor - length : anchor;
}

function getMinSide(imageSide: number): number {
  return Math.min(MIN_CROP_WINDOW_SIDE_PX, imageSide);
}

function getCentreX(rect: CropRect): number {
  return rect.x + rect.width * HALF;
}

function getCentreY(rect: CropRect): number {
  return rect.y + rect.height * HALF;
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
